/*@flow*/
import React from 'react';
import hasProperty from 'lib/hasProperty';
import Blank from 'lib/Blank';
import filterByLabel from 'lib/filterByLabel';
import isObject from 'lib/t/isObject';
import { customStyles } from 'comps/AsyncDropdown';
import type { SelectLocals, LocalsT } from 'lib/proforma/types';
import select from 'lib/proforma/templates/Select';
import omit from 'ramda/src/omit';

const textOpts2LabelOpts = v => {
  if (Blank.is(v)) return [];
  return Array.isArray(v)
    ? v.map(({ value, text }) => ({ value, label: text }))
    : isObject(v)
    ? { value: v.value, label: v.label || v.text }
    : { value: v, label: v };
};

const value2Opts = (value, opts) => {
  if (Blank.is(value)) return [];
  if (Array.isArray(value)) {
    return value.map(v => {
      let ret = { value: v, label: v };
      if (opts) {
        ret = textOpts2LabelOpts(opts.find(v2 => v2.value === value)) || ret;
      }
      return ret;
    });
  }
  let ret = { value, label: value };
  if (opts) {
    ret = textOpts2LabelOpts(opts.find(v => v.value === value)) || ret;
  }
  return ret;
};

const isOpt = v =>
  Array.isArray(v)
    ? v.some(isOpt)
    : hasProperty(v, 'value') && hasProperty(v, 'label');

const values2Opts = cats => {
  return cats
    .map(v => {
      if (isOpt(v)) {
        return v;
      } else if (Array.isArray(v)) {
        const [label, value] = v;
        if (!label) return;
        return { value, label };
      }
      return { value: v, label: v };
    })
    .filter(Boolean);
};

const opts2Value = o => {
  if (!o) return;
  if (Array.isArray(o)) {
    return o.map(v => v.value);
  }
  return o.value;
};

export default (Comp: React$ComponentType<*>): any =>
  select.clone({
    Select: (
      props: SelectLocals &
        LocalsT & { categories?: string[], isMulti: boolean }
    ) => {
      let { value, isMulti, categories, attrs = {}, onChange } = props;
      const defOptLoader = () =>
        categories
          ? Promise.resolve(values2Opts(categories))
          : Promise.resolve(textOpts2LabelOpts(props.options));
      let loadOptions = attrs.loadOptions || defOptLoader;
      let loadValues = attrs.loadValues;
      if (loadValues) {
        loadOptions = () => loadValues().then(res => values2Opts(res));
      }
      const attrs2 = omit(['className'])(attrs);

      return (
        <div className="mb-3">
          <Comp
            {...attrs2}
            loadOptions={loadOptions}
            isMulti={isMulti}
            styles={customStyles}
            value={value2Opts(value, props.options)}
            filterOption={filterByLabel}
            onChange={v => onChange(opts2Value(v))}
            cacheOptions
            defaultOptions
            isClearable
          />
        </div>
      );
    },
  });
